import React, { memo, useCallback, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow, NodeResizeControl, ResizeControlVariant } from 'reactflow';
import { Lock } from 'lucide-react';
import { ShapeNodeData, ShapeType } from '../../types';

const shapeColors: Record<ShapeType, { fill: string; border: string }> = {
  square: { fill: '3b82f6', border: '2563eb' },
  circle: { fill: '10b981', border: '059669' },
  triangle: { fill: 'f59e0b', border: 'd97706' },
  diamond: { fill: '8b5cf6', border: '7c3aed' },
  hexagon: { fill: 'ec4899', border: 'db2777' },
  star: { fill: 'eab308', border: 'ca8a04' },
  parallelogram: { fill: '06b6d4', border: '0891b2' },
  cylinder: { fill: '6366f1', border: '4f46e5' },
};

const ShapeNode = ({
  id,
  data,
  selected,
}: NodeProps<ShapeNodeData>) => {
  const { setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(data.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditLabel(data.label);
  }, [data.label]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleSave = useCallback(() => {
    if (data.onSnapshot) {
      data.onSnapshot();
    }
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              label: editLabel.trim() || data.label,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  }, [id, setNodes, editLabel, data]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditLabel(data.label);
      setIsEditing(false);
    }
    e.stopPropagation();
  };

  const defaultColors = shapeColors[data.shape] || shapeColors.square;
  const fillColor = data.fillColor || defaultColors.fill;
  const borderColor = data.borderColor || defaultColors.border;
  const fillHex = fillColor.startsWith('#') ? fillColor : `#${fillColor}`;
  const borderHex = borderColor.startsWith('#') ? borderColor : `#${borderColor}`;

  const handleColor = selected ? '#3b82f6' : borderHex;

  const renderShape = () => {
    // Shapes fill the node so NodeResizer can size them; preserveAspectRatio="none"
    // lets SVG shapes stretch, and non-scaling-stroke keeps the border crisp.
    const svgFilter = { filter: selected ? `drop-shadow(0 0 2px ${borderHex}40)` : undefined } as React.CSSProperties;

    switch (data.shape) {
      case 'circle':
        return (
          <div
            className="w-full h-full rounded-full flex items-center justify-center transition-all duration-200"
            style={{
              backgroundColor: fillHex,
              border: `3px solid ${borderHex}`,
              boxShadow: selected ? `0 0 0 2px ${borderHex}40` : undefined,
            }}
          >
            {renderLabel()}
          </div>
        );

      case 'triangle':
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 100 87" preserveAspectRatio="none" className="w-full h-full">
              <polygon points="50,5 95,82 5,82" fill={fillHex} stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" style={svgFilter} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center pt-4">
              {renderLabel()}
            </div>
          </div>
        );

      case 'diamond':
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
              <polygon points="50,5 95,50 50,95 5,50" fill={fillHex} stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" style={svgFilter} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              {renderLabel()}
            </div>
          </div>
        );

      case 'hexagon':
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 100 87" preserveAspectRatio="none" className="w-full h-full">
              <polygon points="25,5 75,5 100,43.5 75,82 25,82 0,43.5" fill={fillHex} stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" style={svgFilter} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              {renderLabel()}
            </div>
          </div>
        );

      case 'star':
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
              <polygon points="50,5 61,35 95,35 68,57 79,90 50,70 21,90 32,57 5,35 39,35" fill={fillHex} stroke={borderHex} strokeWidth="2" vectorEffect="non-scaling-stroke" style={svgFilter} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              {renderLabel()}
            </div>
          </div>
        );

      case 'parallelogram':
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 120 60" preserveAspectRatio="none" className="w-full h-full">
              <polygon points="20,5 115,5 100,55 5,55" fill={fillHex} stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" style={svgFilter} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              {renderLabel()}
            </div>
          </div>
        );

      case 'cylinder':
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <svg viewBox="0 0 80 100" preserveAspectRatio="none" className="w-full h-full">
              <ellipse cx="40" cy="15" rx="35" ry="12" fill={fillHex} stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" />
              <rect x="5" y="15" width="70" height="70" fill={fillHex} />
              <line x1="5" y1="15" x2="5" y2="85" stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" />
              <line x1="75" y1="15" x2="75" y2="85" stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" />
              <ellipse cx="40" cy="85" rx="35" ry="12" fill={fillHex} stroke={borderHex} strokeWidth="3" vectorEffect="non-scaling-stroke" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center pt-2">
              {renderLabel()}
            </div>
          </div>
        );

      case 'square':
      default:
        return (
          <div
            className="w-full h-full rounded-lg flex items-center justify-center transition-all duration-200"
            style={{
              backgroundColor: fillHex,
              border: `3px solid ${borderHex}`,
              boxShadow: selected ? `0 0 0 2px ${borderHex}40` : undefined,
            }}
          >
            {renderLabel()}
          </div>
        );
    }
  };

  const renderLabel = () => {
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          value={editLabel}
          onChange={(e) => setEditLabel(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-16 text-center font-semibold text-xs bg-card/80 dark:bg-card/80 rounded px-1 py-0.5 border-none focus:outline-none focus:ring-1 focus:ring-ring text-foreground dark:text-foreground"
          placeholder="Label"
        />
      );
    }
    return (
      <span className="font-semibold text-xs text-white drop-shadow-sm text-center px-1 leading-tight">
        {data.label}
      </span>
    );
  };

  return (
    <>
      {selected && (
        <>
          {/* Edge lines resize a single axis (free stretch). */}
          {(['top', 'right', 'bottom', 'left'] as const).map((pos) => (
            <NodeResizeControl
              key={pos}
              position={pos}
              variant={ResizeControlVariant.Line}
              minWidth={60}
              minHeight={60}
              className="!border-primary !opacity-60"
            />
          ))}
          {/* Corner handles keep the shape's aspect ratio. */}
          {(['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map((pos) => (
            <NodeResizeControl
              key={pos}
              position={pos}
              variant={ResizeControlVariant.Handle}
              keepAspectRatio
              minWidth={60}
              minHeight={60}
              className="!h-2.5 !w-2.5 !bg-primary !border !border-background !rounded-none"
            />
          ))}
        </>
      )}
      <div
        className="group relative w-full h-full flex items-center justify-center"
        style={{ minWidth: 60, minHeight: 60 }}
        onDoubleClick={handleDoubleClick}
      >
      {data.locked && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-amber-400 dark:bg-amber-500 rounded-full flex items-center justify-center shadow-sm z-10">
          <Lock className="w-3 h-3 text-white" />
        </div>
      )}

      {renderShape()}

      <Handle
        type="source"
        position={Position.Top}
        id="top"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: handleColor,
          top: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: handleColor,
          left: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: handleColor,
          bottom: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: handleColor,
          right: '-6px',
        }}
      />
      </div>
    </>
  );
};

export default memo(ShapeNode);
